import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useState } from "react";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { updateBankDetailsSchema } from "../../../schemas/dunzoproviders.schema";
import { errorToast, handleError, successToast } from "../../../utils/common";
import BankDetailsView from "../SingleBorrower/Summary/components/BankDetailsView";
import { is } from "date-fns/locale";
import { updateBusinessDetailsSchema } from "../../../schemas/providers.schema";

const BUSINESS_TYPES = [
  { label: "Proprietorship", value: "PROPRIETORSHIP" },
  { label: "Partnership", value: "PARTNERSHIP" },
  { label: "LLP", value: "LLP" },
  { label: "Private Limited", value: "PRIVATE_LIMITED" },
  { label: "Public Limited", value: "PUBLIC_LIMITED" },
];

const EditBusinessDetailsModal = ({
  showEditBusinessDetailsModal,
  setShowEditBusinessDetailsModal,
  borrowerInfo,
  setBorrowerInfo,
}: any) => {
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();

  const handleSubmit = async (values: any, actions: any) => {
    const updatedBusinessDetails = {
      ...borrowerInfo?.business_details,
      business_type: values.business_type,
      fssai_no: values.fssai_no,
      tin_no: values.tin_no,
      cin_no: values.cin_no,
      uin_no: values.uin_no,
      dl_no: values.dl_no,
      cst_no: values.cst_no,
    };

    const payload = {
      borrower_id: borrowerInfo?.borrower_id,
      business_details: updatedBusinessDetails,
    };

    setLoading(true);

    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (result?.data?.status === API_RESULT.SUCCESS) {
        setShowEditBusinessDetailsModal(false);

        setBorrowerInfo((prev: any) => ({
          ...prev,
          business_details: updatedBusinessDetails,
        }));

        successToast("Business details updated successfully");
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={showEditBusinessDetailsModal}
      onClose={() => setShowEditBusinessDetailsModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setShowEditBusinessDetailsModal(false)}
      >
        Update Business Details
      </CustomDialogTitle>
      <DialogContent sx={{ overflowY: "scroll" }}>
        <Formik
          initialValues={{
            business_type: borrowerInfo?.business_details?.business_type || "",
            fssai_no: borrowerInfo?.business_details?.fssai_no || "",
            tin_no: borrowerInfo?.business_details?.tin_no || "",
            cin_no: borrowerInfo?.business_details?.cin_no || "",
            uin_no: borrowerInfo?.business_details?.uin_no || "",
            dl_no: borrowerInfo?.business_details?.dl_no || "",
            cst_no: borrowerInfo?.business_details?.cst_no || "",
          }}
          validationSchema={updateBusinessDetailsSchema}
          onSubmit={(values: any, actions: any) => {
            actions.setSubmitting(false);
            handleSubmit(values, actions);
          }}
        >
          {(props) => {
            const { values, setFieldValue, handleBlur, errors, touched } =
              props;
            return (
              <Form>
                <Stack direction="row" justifyContent="space-between">
                  <TextField
                    select
                    fullWidth
                    id="business_type"
                    label="Business Type"
                    size="small"
                    value={values.business_type}
                    sx={{ mt: 1.5 }}
                    onChange={(e) =>
                      setFieldValue("business_type", e.target.value)
                    }
                    onBlur={handleBlur}
                    helperText={
                      errors.business_type && touched.business_type
                        ? errors.business_type
                        : ""
                    }
                    error={Boolean(
                      touched.business_type && errors.business_type,
                    )}
                    InputLabelProps={{ required: true }}
                  >
                    <MenuItem value="">Select business type</MenuItem>
                    {BUSINESS_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="fssai_no"
                    label="FSSAI Number"
                    size="small"
                    value={values.fssai_no || ""}
                    sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                    onChange={(e) => setFieldValue("fssai_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.fssai_no && touched.fssai_no ? errors.fssai_no : ""
                    }
                    error={errors.fssai_no && touched.fssai_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    id="tin_no"
                    label="TIN Number"
                    size="small"
                    value={values.tin_no || ""}
                    sx={{ mt: 1.5 }}
                    onChange={(e) => setFieldValue("tin_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.tin_no && touched.tin_no ? errors.tin_no : ""
                    }
                    error={errors.tin_no && touched.tin_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="cin_no"
                    label="CIN Number"
                    size="small"
                    value={values.cin_no || ""}
                    sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                    onChange={(e) => setFieldValue("cin_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.cin_no && touched.cin_no ? errors.cin_no : ""
                    }
                    error={errors.cin_no && touched.cin_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    id="uin_no"
                    label="UIN Number"
                    size="small"
                    value={values.uin_no || ""}
                    sx={{ mt: 1.5 }}
                    onChange={(e) => setFieldValue("uin_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.uin_no && touched.uin_no ? errors.uin_no : ""
                    }
                    error={errors.uin_no && touched.uin_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="dl_no"
                    label="DL Number"
                    size="small"
                    value={values.dl_no || ""}
                    sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                    onChange={(e) => setFieldValue("dl_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.dl_no && touched.dl_no ? errors.dl_no : ""
                    }
                    error={errors.dl_no && touched.dl_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    id="cst_no"
                    label="CST Number"
                    size="small"
                    value={values.cst_no || ""}
                    sx={{ mt: 1.5 }}
                    onChange={(e) => setFieldValue("cst_no", e.target.value)}
                    onBlur={handleBlur}
                    helperText={
                      errors.cst_no && touched.cst_no ? errors.cst_no : ""
                    }
                    error={errors.cst_no && touched.cst_no ? true : false}
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                </Stack>

                <Box textAlign="center" mt={3}>
                  <LoadingButton
                    size="medium"
                    color="primary"
                    loading={loading}
                    loadingIndicator={
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    }
                    variant="contained"
                    type="submit"
                  >
                    Update
                  </LoadingButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditBusinessDetailsModal;
